import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Target, ShieldCheck, Zap } from "lucide-react";
import { trackEvent } from "@/lib/api";

// Editable: replace with Jocelyn's Square booking / application link.
const BOOKING_URL = "https://www.empresscoachingandconsulting.com/strategy-session";

const EXEC_IMG =
  "https://images.unsplash.com/photo-1614786269829-d24616faf56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwcHJvZmVzc2lvbmFsJTIwYnVzaW5lc3N3b21hbnxlbnwwfHx8fDE3ODM1MjY0MTB8MA&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

const pillars = [
  { icon: Phone, title: "4 calls a month", body: "Consistent, private sessions built entirely around you — not a curriculum." },
  { icon: Target, title: "A strategy that's yours", body: "Clear priorities for business and personal breakthroughs, revisited every session." },
  { icon: ShieldCheck, title: "Total accountability", body: "Someone in your corner who holds the standard when momentum dips." },
  { icon: Zap, title: "A direct line", body: "Access to Jocelyn between calls so you never stall waiting a week for support." },
];

export default function Coaching() {
  useEffect(() => {
    trackEvent("coaching_view", null);
  }, []);

  return (
    <div className="min-h-screen bg-[#EAE6E1] text-[#2A1B2E]">
      {/* Nav */}
      <nav className="absolute top-0 left-0 right-0 z-10">
        <div className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
          <Link to="/" className="font-serif text-xl tracking-tight text-[#EAE6E1]" data-testid="coaching-brand-home">
            Empress Coaching
          </Link>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coaching-nav-book-btn"
            className="text-sm font-semibold uppercase tracking-[0.15em] bg-[#EAE6E1] text-[#26262B] rounded-md px-5 py-2.5 hover:bg-white transition-colors duration-300"
          >
            Book a session
          </a>
        </div>
      </nav>

      {/* Hero — executive / asphalt */}
      <section className="relative bg-[#26262B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-[#EAE6E1]">
            <motion.p variants={fade} initial="hidden" animate="show" custom={0}
              className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-6">
              Private 1:1 Coaching with Jocelyn
            </motion.p>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
              You know where you're going. Let's get there faster.
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-6 text-lg text-[#EAE6E1]/75 max-w-md leading-relaxed">
              High-touch coaching for capable women who've outgrown their current
              setup — in business, in life, or both. Four private calls a month,
              total access, and a strategy built entirely around you.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-9">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="coaching-hero-book-btn"
                className="group inline-flex items-center gap-2 bg-[#64246A] text-white rounded-md px-8 py-4 font-semibold hover:bg-[#4A1A50] transition-colors duration-300"
              >
                Book Your Strategy Session
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-lg overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
          >
            <img src={EXEC_IMG} alt="Executive coaching" className="w-full h-[380px] md:h-[520px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-6">Who this is for</p>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
            You're capable, driven, and done spinning your wheels.
          </h2>
          <p className="mt-6 text-lg text-[#2A1B2E]/75 leading-relaxed">
            You don't need another course or a bigger to-do list. You need a sharp
            thinking partner, real accountability, and space to make the calls that
            actually move you forward — quickly.
          </p>
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-4">The engagement</p>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight max-w-2xl">
          Four calls a month. Total access. Total accountability.
        </h2>
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="bg-white border border-[#2A1B2E]/10 rounded-lg p-8 flex gap-5"
              data-testid={`coaching-pillar-${i}`}
            >
              <div className="shrink-0 w-12 h-12 rounded-md bg-[#26262B] flex items-center justify-center">
                <p.icon className="w-6 h-6 text-[#A87CA0]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-2xl mb-2">{p.title}</h3>
                <p className="text-[#2A1B2E]/70 leading-relaxed">{p.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="rounded-lg bg-[#26262B] text-[#EAE6E1] px-8 md:px-16 py-16 md:py-20 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-2xl mx-auto">
            Ready to move at your speed?
          </h2>
          <p className="mt-5 text-lg text-[#EAE6E1]/80 max-w-xl mx-auto">
            Start with a strategy session. We'll map your fastest path and see if
            1:1 is the right fit.
          </p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coaching-footer-book-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#64246A] text-white rounded-md px-8 py-4 font-semibold hover:bg-[#4A1A50] transition-colors duration-300"
          >
            Book Your Strategy Session <ArrowRight className="w-4 h-4" />
          </a>
          <div className="mt-6">
            <Link to="/quiz" className="text-sm text-[#EAE6E1]/60 hover:text-[#EAE6E1] transition-colors" data-testid="coaching-quiz-link">
              Not sure yet? Take the 2-minute quiz
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A1B2E]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#2A1B2E]/60">
          <span className="font-serif text-base text-[#2A1B2E]">Empress Coaching &amp; Consulting</span>
          <span>Lead from within. Create what matters.</span>
        </div>
      </footer>
    </div>
  );
}
